<script>
    import {createEventDispatcher} from 'svelte';
    import {fade} from 'svelte/transition';
    import {PaginationNav} from 'svelte-paginate';
    import {sgettext} from '../../i18n.js';
    import Button from '../Button.svelte';

    export let dataListRow = false;
    export let showHeader = true;
    export let noHeader = false;
    export let striped = false;
    export let hover = true;
    export let small = false;
    export let showFooter = false;
    export let saving = false;
    export let total = 0;
    export let perPage = 1;
    export let currentPage = 1;
    export let changePage = () => {};
    export let footerColSpan = 1;
    export let schema = {};
    export let onRowClick = (event, row) => {
    };
    export let rowActions = null;
    export let dragMode = '';
    export let aggregatedColumns = null;
    export let sortFunction = () => {
    };
    export let emptyLabel = sgettext('$$NONE$$');
    export let tableId = "";
    export let field = '';
    export let stickyHeader = false;
    export let allInstances = false;
    export let showStatus = false;
    export let defaultCellValue = '';
    export let tooltip = '';

    export let columns = {
        col1: {
            label: 'Column 1'
        }
    };

    export let rows = [
        {
            id: 1,
            col1: "data for column 1"
        }
    ];

    $: showHeader = noHeader? false : rows.length > 0;

    const dispatch = createEventDispatcher();

    let columnKeys = Object.keys(columns);
    let draggedRow = null;

    if (changePage) {
        changePage({page: 1});
    }

    function onBodyRowDragStart(event, row) {
        draggedRow = row;
    }

    function onBodyRowDragEnter(event, row) {
        reorderData(draggedRow, row);
    }

    function onBodyRowDragOver(event, row) {
        event.dataTransfer.dropEffect = 'move';
    }

    function onBodyRowDragEnd(event) {
        draggedRow = null;
    }

    function onBodyRowDrop(event) {
        dispatch('reordered', {
            rows
        });
    }

    function reorderData(source, target) {
        if (source && target) {
            const sourceSequence = source.sequence;
            const targetSequence = target.sequence;

            source.sequence = targetSequence;
            target.sequence = sourceSequence;

            rows = [...rows];
            if (sortFunction) {
                rows.sort(sortFunction);
            }
        }
    }

    function renderColumn(row, columnName) {
        const obj = getObject(row, columnName);
        if (obj) {
            let keyPath = columnName;

            if (columns[columnName].hasOwnProperty('keyPath')) {
                keyPath = columns[columnName].keyPath;
            }

            return obj[keyPath];
        }
        return defaultCellValue;
    }

    function handleChange(event) {
        dispatch('handleChange', {
            event
        });
    }

    function getObject(row, columnName) {
        /*
            Retrieve the object given a django-style double__underscore__attribute__name specified as the
            sourceObjectName.

            If no sourceObjectName is specified for the column, the row is simple returned as-is.

            e.g.
            row = {id: 1, inner_row: {id: 56}};

            columns = {
                id: {
                    label: "ID"
                },
                inner: {
                    label: "INNER",
                    sourceObjectName: 'inner_row'
                }
            }

            getObject(row, 'inner')
            > {id: 56}
            getObject(row, 'id')
            > {id: 1, inner_row: {id: 56}}
         */

        let obj = row;

        if (columns[columnName].hasOwnProperty('sourceObjectName')) {
            let sourceObjectName = columns[columnName].sourceObjectName;

            for (let attr of sourceObjectName.split('__')) {
                obj = obj[attr];
            }
        }

        return obj;
    }

</script>

<style>
    .custom-nav :global(.pagination-nav) {
        display: flex;
        justify-content: flex-end;
        float: right;
        width: 60%;
        font-weight: 400;
    }

    .custom-nav :global(.option) {
        width: 15%;
        border: 1px solid #ced4da;
        text-align: center;
        cursor: pointer;
        background-color: #e9ecef;
    }

    .custom-nav :global(.option.active) {
        cursor: pointer;
        background-color: #ced4da;
    }

    .custom-nav :global(.option.disabled) {
        opacity: 0.25;
        pointer-events: none;
        cursor: auto;
    }

    .actions {
        text-align: right;
    }

    .actions i {
        cursor: pointer;
    }

    .actions :global(.btn) {
        margin-left: 12px;
    }

    .actions :global(.btn:first-child) {
        margin-left: 0;
    }

    .actions i:hover {
        color: #dd5900;
    }

    .datalist-row:hover, .active {
        background-color: rgba(255, 214, 145, 0.5) !important;
    }

    .sw.table thead th {
        vertical-align: middle;
    }

    .draggable {
        cursor: move;
    }

    #hazardControlEditorId td, #hazardControlEditorId th{
        border-top: none;
    }

    #hazardControlEditorId thead th{
        border-bottom: none;
    }

    #hazardControlTableId td, #hazardControlTableId th{
        border-top: none;
    }

    #hazardControlTableId thead th{
        border-bottom: none;
    }

    .table tr:first-child td {
        border-top: none !important;
    }
</style>

<table
        id={tableId}
        transition:fade|local
        class="sw table mb-0 border"
        class:table-sm={small}
        class:table-hover={hover}
        class:table-striped={striped}>
    {#if showHeader}
        <thead class="thead-light" style={stickyHeader ? 'position: sticky; top: -1px;' : 'none'}>
        <tr>
            {#each columnKeys as header}
                {#if columns[header].min_width}
                    <th style={`min-width: ${columns[header].min_width}`}>{columns[header].label}</th>
                {:else if columns[header].tooltip !== undefined}
                    <th style={`width: ${columns[header].width}`}>{columns[header].label}
                      <i
                        class="fa fa-question-circle"
                        title={columns[header].tooltip}
                        on:click={columns[header].tooltipClickEvent ? columns[header].tooltipClickEvent : null}
                      />
                    </th>
                {:else}
                    <th style={`width: ${columns[header].width}`}>{columns[header].label}</th>
                {/if}
            {/each}
            {#if aggregatedColumns}
                {#each Object.keys(aggregatedColumns) as ac}
                    <th style={`width: ${aggregatedColumns[ac].width}`}>{aggregatedColumns[ac].label}</th>
                {/each}
            {/if}
            {#if rowActions}
                <th style="min-width: 80px; text-align: right;">{sgettext('$$ACTIONS$$')}</th>
            {/if}
        </tr>
        </thead>
    {:else}
        <thead>
        <tr>
            <td class="p-0"></td>
        </tr>
        </thead>
    {/if}
    <tbody>
    {#each rows as row, i (row.id && row.name? `${row.id}_${row.name}` : i)}
        <tr
                transition:fade|local
                class:datalist-row={dataListRow}
                class:active={'selected' in row && row.selected}
                class:draggable={dragMode === 'bodyRow'}
                draggable={dragMode === 'bodyRow'}
                on:dragstart={event => onBodyRowDragStart(event, row)}
                on:dragenter={event => onBodyRowDragEnter(event, row)}
                on:dragover|preventDefault={event => onBodyRowDragOver(event, row)}
                on:drop|preventDefault={onBodyRowDrop}
                on:dragend={onBodyRowDragEnd}
                key={row.id ? row.id : i}
                on:click={(event) => onRowClick(event, row)}
                style={`${row?.backgroundColor ? `${row.backgroundColor}` : ''}${(row.invalid_hazard_control) ? "border:1px solid red;" : "border:none;"}${row?.deleted ? ";display: none": ""}`}>
            {#each columnKeys as col}
                <td style={`${columns[col].style}${(allInstances && row.start_time && !row.expired && !row.end_time) ? ';background-color:#e5f5ff': ''}`}>
                    {#if columns[col].renderer && (row?.type != "APM" || columns[col].props?.allowAPMEdit) }
                        <svelte:component
                            id={`${col}-${i}`}
                            key={columns[col].keyPath ? columns[col].keyPath : col}
                            this={columns[col].renderer}
                            bind:data={row[col]}
                            {...columns[col].props}
                            defaultValue={defaultCellValue}
                            {schema}
                            row={getObject(row, col)}
                            on:handleChange={handleChange}/>
                    {:else}
                        {#if showStatus}
                            {sgettext(renderColumn(row, col))}
                        {:else}
                            {renderColumn(row, col)}
                        {/if}
                    {/if}
                </td>
            {/each}
            {#if aggregatedColumns}
                {#each Object.keys(aggregatedColumns) as ac}
                    <td>
                        {#if aggregatedColumns[ac].renderer}
                            <svelte:component
                                id={`${ac}-${i}`}
                                key={ac}
                                this={aggregatedColumns[ac].renderer}
                                {...aggregatedColumns[ac].props}
                                {schema}
                                {row}
                                on:handleChange={handleChange}/>
                        {:else}
                            {''}
                        {/if}
                    </td>
                {/each}
            {/if}
            {#if rowActions}
                {#if row.hasOwnProperty('rowActions')}
                    <td class="actions d-flex flex-nowrap justify-content-end">
                        {#each Object.keys(row.rowActions) as k}
                            {#if row.rowActions[k].hasOwnProperty('iconClass')}
                                <span on:click={(event) => row.rowActions[k].handler(event, row)}>
                                    <i class={row.rowActions[k].iconClass}/>
                                </span>
                            {:else}
                                <Button
                                    name="{row.rowActions[k].label}"
                                    type="{row.rowActions[k].btnClass}"
                                    disabled="{row.rowActions[k].key === 'save' && saving ||  row.rowActions[k].hasOwnProperty('disabled') && row.rowActions[k].disabled(row)}"
                                    sending="{row.rowActions[k].key === 'save' && saving}"
                                    style="{row.rowActions[k].width ? `width: ${row.rowActions[k].width}` : ''}"
                                    on:click={(event) => row.rowActions[k].handler(event, row)}
                                />
                            {/if}
                        {/each}
                    </td>
                {:else}
                    <td class="actions d-flex flex-nowrap justify-content-end">
                        {#each Object.keys(rowActions) as k}
                            {#if rowActions[k].hasOwnProperty('renderer')}
                                <svelte:component
                                    id={`${rowActions[k]}-${i}`}
                                    this={rowActions[k].renderer}
                                    {...rowActions[k].props}
                                    {row}
                                    />
                            {:else if rowActions[k].hasOwnProperty('iconClass') && (!rowActions[k]?.apmReadonly || row?.type == "NORMAL")}
                                <span on:click={(event) => rowActions[k].handler(event, row)}>
                                    <i class={rowActions[k].iconClass}/>
                                </span>
                            {:else if row?.type != "APM"  && !rowActions[k]?.apmReadonly }
                                <Button
                                    name="{rowActions[k].label}"
                                    type="{rowActions[k].btnClass}"
                                    disabled="{rowActions[k].key === 'save' && saving || rowActions[k].hasOwnProperty('disabled') && rowActions[k].disabled(row)}"
                                    sending="{rowActions[k].key === 'save' && saving}"
                                    style="{rowActions[k].width ? `width: ${rowActions[k].width}` : ''}"
                                    on:click={(event) => rowActions[k].handler(event, row)}
                                />
                            {/if}
                        {/each}
                    </td>
                {/if}
            {/if}
        </tr>
        {#if tableId === 'hazardControlEditorId' && row.invalid_hazard_control}
            <i class="fa fa-exclamation-circle text-danger" style="display: flex;margin-top: -45px;margin-left: -1px;"/>
        {:else if field === 'hazard' && row.invalid_hazard_control}
            <i class="fa fa-exclamation-circle text-danger" style="display: flex;margin-top: -57px;padding-left: 3px;padding-right: 3px;"/>
        {/if}
    {/each}
    {#if rows.length === 0}
        <tr>
            <td>{emptyLabel}</td>
        </tr>
    {/if}
    <slot name="infiniteScroll"/>
    </tbody>
    {#if showFooter}
        <tfoot class="bg-gray-300">
        <tr>
            <th colspan={footerColSpan}>
                <span class="custom-nav">
                    <PaginationNav
                        totalItems={total}
                        pageSize={perPage}
                        currentPage={currentPage}
                        limit={1}
                        showStepOptions={true}
                        on:setPage={(e) => {
                            currentPage = e.detail.page;
                            changePage(e.detail);
                            }}
                    />
                </span>
            </th>
        </tr>
        </tfoot>
    {/if}
</table>
